import { AccessibilityStackManager } from '@owc-utils/common/AccessibilityFocusStackHelper';
import { FOCUS_STACK_HEADER_CONTEXT } from './Header.configs';

import { HeaderFlyoutData, HeaderFlyoutMethods, HeaderFlyoutProps } from './HeaderFlyout';

export type HeaderFlyoutInstance = Pick<HeaderFlyoutData, 'isFirstLevelDesktop' | 'accessibilityHelper'> &
  Pick<HeaderFlyoutProps, 'currentFlyoutLevel'> &
  Pick<HeaderFlyoutMethods, 'handleFlyoutCloseClick'>;

const _handleLeftArrowClick = (
  keyboardEvent: KeyboardEvent,
  globalFocusStack: AccessibilityStackManager,
  context: HeaderFlyoutInstance,
) => {
  if (!context.isFirstLevelDesktop) {
    context.handleFlyoutCloseClick(false);
    keyboardEvent.stopImmediatePropagation();

    globalFocusStack.popLastFocusedElementOnStack(FOCUS_STACK_HEADER_CONTEXT, true, true);
  }
};

const _handleRightArrowClick = (
  keyboardEvent: KeyboardEvent,
  flyoutChainSize: number,
  globalFocusStack: AccessibilityStackManager,
  context: HeaderFlyoutInstance,
) => {
  if (flyoutChainSize === context.currentFlyoutLevel) {
    const focusedElement = context.accessibilityHelper.tryToOpenFocusedElement(true);

    if (focusedElement) {
      keyboardEvent.stopImmediatePropagation();

      globalFocusStack.pushToFocusStack(focusedElement, FOCUS_STACK_HEADER_CONTEXT);
    }
  }
};

const _handleLinkClick = (event: KeyboardEvent) => {
  const target = event.target as HTMLElement;

  target?.click();
  event.stopPropagation();
};

/**
 * Keeps camelCase syntax and enforces first letter into
 * lowercase
 *
 * @param string: String to be lowercased (first letter only)
 */
export function lowercaseFirstLetter(string: string) {
  string = string.trim();

  return string.charAt(0).toLowerCase() + string.slice(1);
}

/**
 * TODO: Currently not used. Check if needed after header buttons for cart, favorite etc are all slots
 * Takes the array of values and returns a filtered and sorted array based on the
 * arrayOfIds. Objects inside the arrayOfValues are required to have an ID that can
 * be matched on the arrayOfIDs
 *
 * ex:
 * arrayOfValues = [{id: 10, color: orange}, {id: 11, color: red}, {id: 12, color: yellow}]
 * arrayOfIds = [12, 10];
 *
 * sortedArray = [{id: 12, color: yellow}, {id: 10, color: orange}]
 */
export function filterUniqueOrderedById<K extends { id: string | number }>(
  orderedIds: (string | number)[],
  sourceValues: K[],
): K[] {
  const target = Array(orderedIds.length);

  for (let index = 0; index < orderedIds.length; index++) {
    const element = sourceValues.find(element => element.id === orderedIds[index]);
    target[index] = element;
  }

  return target.filter(element => element != null);
}

/**
 * Builds a  runtime componentId based on the elemenet type, label and its position on the
 * list (if any)
 * This is useful for example to mock the componentId that is generated by the regestry
 * so  that the store logic still works even if we are working with json
 *
 * @param elementType = HtmlElement type
 * @param elementLabel = Element's label
 * @param index = List index, if none is provided 0 is used as a default
 * @returns the generated id as a string
 */
export function buildHeaderComponentId(elementType: string, elementLabel: string, index = 0): string {
  return `owc-header-${elementType}-${elementLabel.replace(/ /g, '')}-${index}`;
}

export function headerFlyoutFocusHandler(
  event: KeyboardEvent,
  globalFocusStack: AccessibilityStackManager,
  flyoutChainSize: number,
  context: HeaderFlyoutInstance,
) {
  const keyboardEvent = event as KeyboardEvent;

  switch (keyboardEvent.code) {
    case 'ArrowUp':
      context.accessibilityHelper.focusOnPreviousElement();
      keyboardEvent.stopImmediatePropagation();
      break;
    case 'ArrowRight':
      _handleRightArrowClick(keyboardEvent, flyoutChainSize, globalFocusStack, context);
      break;
    case 'ArrowDown':
      context.accessibilityHelper.focusOnNextElement();
      keyboardEvent.stopImmediatePropagation();
      break;
    case 'ArrowLeft':
      _handleLeftArrowClick(keyboardEvent, globalFocusStack, context);
      break;
    case 'Home':
      context.accessibilityHelper.focusOnFirstElement();

      keyboardEvent.preventDefault();
      keyboardEvent.stopImmediatePropagation();
      break;
    case 'End':
      context.accessibilityHelper.focusOnLastElement();

      keyboardEvent.preventDefault();
      keyboardEvent.stopImmediatePropagation();
      break;
    case 'Tab':
      context.handleFlyoutCloseClick(true);
      globalFocusStack.resetWithContext(FOCUS_STACK_HEADER_CONTEXT);
      break;
    case 'Escape':
      context.handleFlyoutCloseClick(true);
      globalFocusStack.popLastFocusedElementOnStack(FOCUS_STACK_HEADER_CONTEXT);
      break;
    case 'Enter':
      keyboardEvent.stopImmediatePropagation();
      break;
    case 'Space':
      _handleLinkClick(keyboardEvent);
      break;
  }
}
